import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  readStoredTheme,
  resolveTheme,
  storeTheme,
  systemPrefersDark,
  type ResolvedTheme,
  type ThemePreference,
} from '@/lib/theme';

interface UiState {
  sidebarOpen: boolean;
  /** What the guest asked for. `system` is the default — see `lib/theme.ts`. */
  theme: ThemePreference;
  /**
   * What that currently resolves to.
   *
   * Kept in the store rather than recomputed at every call site because it is
   * read by things that are not CSS — the mark's lockup, the toast surface —
   * and because under `system` it changes without any action being dispatched.
   */
  resolvedTheme: ResolvedTheme;
  activeBranchId: string | null;
}

const initialPreference = readStoredTheme();

const initialState: UiState = {
  sidebarOpen: true,
  theme: initialPreference,
  resolvedTheme: resolveTheme(initialPreference, systemPrefersDark()),
  activeBranchId: localStorage.getItem('venue.branch'),
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    /**
     * The guest's choice. The DOM is not touched here: `App` owns applying it,
     * so that the same effect handles a preference change and the phone
     * flipping to dark at sunset, and a reducer stays a pure function.
     */
    setTheme(state, action: PayloadAction<ThemePreference>) {
      state.theme = action.payload;
      state.resolvedTheme = resolveTheme(action.payload, systemPrefersDark());
      storeTheme(action.payload);
    },
    /** The OS changed its mind while the app was open. */
    systemThemeChanged(state, action: PayloadAction<boolean>) {
      state.resolvedTheme = resolveTheme(state.theme, action.payload);
    },
    setActiveBranch(state, action: PayloadAction<string | null>) {
      state.activeBranchId = action.payload;
      if (action.payload) localStorage.setItem('venue.branch', action.payload);
      else localStorage.removeItem('venue.branch');
    },
  },
});

export const { toggleSidebar, setTheme, systemThemeChanged, setActiveBranch } = uiSlice.actions;
export default uiSlice.reducer;
